import { PRIORITY, STATUS } from '@/entities/alert/model/types';
import type { AxiosRequestConfig } from 'axios';
import z from 'zod';
import { api } from '../../axios.instance';
import { responseContract } from '../../lib';

const _GetAlertByIdRequestDtoSchema = z.object({
  id: z.number(),
});

const AlertResolutionOptionsSchema = z.array(
  z.object({
    id: z.number(),
    isSelected: z.boolean(),
    label: z.string(),
  })
);

const AlertResolutionsSchema = z.array(
  z.object({
    id: z.number(),
    label: z.string(),
    sortOrder: z.number(),
  })
);

const SelectedOptionSchema = z.object({
  allowMultipleResolutions: z.boolean(),
  id: z.number(),
  label: z.string(),
  points: z.number(),
  pointsResetDays: z.number(),
  priority: z.enum(PRIORITY),
  repeatAlertPoints: z.number().nullable(),
  repeatEnabled: z.boolean(),
  repeatFrequencyDays: z.number().nullable(),
  resolutions: AlertResolutionsSchema,
  sortOrder: z.number(),
  timeToResolveDays: z.number(),
});

const OptionsSchema = z.object({
  allowMultipleResolutions: z.boolean(),
  id: z.number(),
  label: z.string(),
  points: z.number(),
  pointsResetDays: z.number(),
  priority: z.enum(PRIORITY),
  repeatAlertPoints: z.number().nullable(),
  repeatEnabled: z.boolean(),
  repeatFrequencyDays: z.number().nullable(),
  resolutions: AlertResolutionsSchema,
  sortOrder: z.number(),
  timeToResolveDays: z.number(),
});

const GetAlertsByIdResponseDtoSchema = z.object({
  createdAt: z.string(),
  daysOpen: z.number(),
  facility: z.object({
    id: z.number(),
    name: z.string(),
  }),
  id: z.number(),
  incidentDateTime: z.string(),
  isOverdue: z.boolean(),
  resident: z
    .object({
      id: z.number(),
      fullName: z.string(),
      levelOfCare: z.string().nullish(),
    })
    .nullable(),
  resolutionOptions: AlertResolutionOptionsSchema,
  status: z.enum(STATUS),
  alertConfig: z.object({
    displayName: z.string(),
    dropdownName: z.string().nullable(),
    enableDropdown: z.boolean(),
    id: z.number(),
    options: z.array(OptionsSchema),
    originalName: z.string(),
  }),
  daysRemaining: z.number().nullable(),
  dueAt: z.string().nullable(),
  incidentType: z.string().nullable(),
  priority: z.enum(PRIORITY).nullable(),
  resolvedAt: z.string().nullable(),
  selectedOption: SelectedOptionSchema.nullable(),
  resolvedByName: z.string().nullable(),
});

export type GetAlertByIdRequestDto = z.infer<
  typeof _GetAlertByIdRequestDtoSchema
>;
export type GetAlertsByIdResponseDto = z.infer<
  typeof GetAlertsByIdResponseDtoSchema
>;

export const getAlertById = (
  params: GetAlertByIdRequestDto,
  config?: AxiosRequestConfig<GetAlertByIdRequestDto>
) => {
  return api
    .get(`alerts/${params.id}`, { ...config })
    .then(responseContract(GetAlertsByIdResponseDtoSchema));
};
